import { useEffect, useRef, useState } from 'react';
import { Textarea } from '@chakra-ui/react';
import {
  ArrowCta,
  Button,
  Heading,
  OptionRow,
  Text,
  VStack,
} from 'components/ui';
import { StickyCtaLayout } from '../ui/StickyCtaLayout';
import HeaderNav from '../HeaderNav/HeaderNav';
import { useFormData } from '../../utils/formReducer.js';
import { FORM_ACTIONS } from '../../utils/formActions.js';
import { useTracking } from '../../hooks/useTracking';
import {
  CurrentStatusAnswer,
  CurrentStatus as CurrentStatusPayload,
} from 'api/types';
import {
  FORM_VIEW,
  FormViews,
  TRACKING_CURRENT_STATUS_SELECTED,
  TRACKING_CURRENT_STATUS_SKIPPED,
} from './constants';

// The copy for each answer. The values themselves live with the API types,
// since the backend validates them.
const CURRENT_STATUS_OPTIONS: {
  answer: CurrentStatusAnswer;
  label: string;
}[] = [
  {
    answer: CurrentStatusAnswer.Newbie,
    label: "I haven't really addressed my symptoms yet",
  },
  {
    answer: CurrentStatusAnswer.Learning,
    label: "I'm starting to understand what sets off my symptoms",
  },
  {
    answer: CurrentStatusAnswer.NeedMoreHelp,
    label: "I've tried a lot already, and most of it hasn't worked",
  },
  {
    answer: CurrentStatusAnswer.Maintaining,
    label: 'I found what works and want help maintaining control',
  },
  { answer: CurrentStatusAnswer.Other, label: 'Something else' },
];

// Same field wash and focus treatment as our Input and as the feedback note,
// at the 12px radius the option rows above it use.
const DESCRIPTION_FIELD_PROPS = {
  bg: 'rgba(4, 51, 56, 0.05)',
  borderColor: 'transparent',
  borderWidth: '1px',
  color: 'secondary.500',
  fontFamily: 'body',
  fontSize: '15px',
  minH: '96px',
  px: '16px',
  py: '15px',
  resize: 'vertical',
  rounded: 'row',
  w: '100%',
  focusRing: 'none',
  _placeholder: { color: 'secondary.200', opacity: 0.4 },
  _focus: {
    bg: 'white',
    borderColor: 'secondary.200',
    outline: 'none',
  },
} as const;

type Props = {
  onNext: () => void;
};

export function CurrentStatus({ onNext }: Props) {
  const { state: formData, dispatch } = useFormData();
  const { track } = useTracking();

  const storedAnswer: CurrentStatusAnswer | null =
    formData?.current_status?.answer ?? null;
  const storedDescription: string = formData?.current_status?.description ?? '';

  const [answer, setAnswer] = useState<CurrentStatusAnswer | null>(
    storedAnswer
  );
  const [description, setDescription] = useState(storedDescription);

  const isOther = answer === CurrentStatusAnswer.Other;
  const canContinue = !!answer && (!isOther || !!description.trim());

  // On a phone the field opens below the fold, behind the sticky CTA, so the
  // prompt to write something is easy to miss. Bring it into view when it
  // appears. Centring it keeps it clear of the CTA.
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  // The ref is only set while the field is on screen, so this is a no-op for
  // every other answer.
  useEffect(() => {
    descriptionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [isOther]);

  const save = (payload: CurrentStatusPayload | null) =>
    dispatch({
      type: FORM_ACTIONS.UPDATE_FORM_FIELD,
      fieldName: 'current_status',
      payload,
    });

  // The browser back and forward buttons leave this step without going through
  // Next, and that unmounts the screen, so the answer goes into the form state
  // as soon as it is complete. Every other step of the flow writes on
  // selection too.
  //
  // "Something else" with no text yet is incomplete and stores nothing,
  // because the backend rejects an `other` that carries no description.
  const record = (
    nextAnswer: CurrentStatusAnswer | null,
    nextDescription: string
  ) => {
    const text = nextDescription.trim();

    if (!nextAnswer) return save(null);

    if (nextAnswer === CurrentStatusAnswer.Other) {
      return save(text ? { answer: nextAnswer, description: text } : null);
    }

    return save({ answer: nextAnswer });
  };

  const selectAnswer = (nextAnswer: CurrentStatusAnswer) => {
    setAnswer(nextAnswer);
    record(nextAnswer, description);
  };

  const changeDescription = (nextDescription: string) => {
    setDescription(nextDescription);
    record(answer, nextDescription);
  };

  const handleNext = () => {
    if (!canContinue) return;

    track(TRACKING_CURRENT_STATUS_SELECTED, { answer });
    onNext();
  };

  const handleSkip = () => {
    save(null);
    track(TRACKING_CURRENT_STATUS_SKIPPED, null);
    onNext();
  };

  return (
    <StickyCtaLayout
      actionArea={
        <>
          <ArrowCta
            disabled={!canContinue}
            onClick={handleNext}
            data-testid="onboarding-current-status-next-button"
          >
            Next
          </ArrowCta>

          <Button
            bgColor={'transparent'}
            onClick={handleSkip}
            variant={'ghost'}
            h={'auto'}
            minH={'auto'}
            py={0}
            w={'100%'}
            mt={4}
            data-testid="onboarding-current-status-skip-button"
          >
            <Text
              color={'secondary.500'}
              fontSize={'14px'}
              fontWeight={600}
              textDecoration={'underline'}
            >
              Skip for now
            </Text>
          </Button>
        </>
      }
      header={
        <HeaderNav
          screenName={FORM_VIEW[FormViews.questionCurrentStatus].trackingLabel}
          showWhatIsAybleModal
        />
      }
      maxWidth="formWidth"
      testID="onboarding-current-status"
    >
      <VStack gap={8} w="100%">
        <VStack className="header" w="100%" gap={2}>
          <Heading textStyle="heading.h2">
            Which best describes where you&apos;re at right now?
          </Heading>
          <Text textAlign="center">Pick the one that fits best.</Text>
        </VStack>

        <VStack gap={2} w="100%">
          {CURRENT_STATUS_OPTIONS.map((option) => (
            <OptionRow
              key={option.answer}
              isSelected={answer === option.answer}
              onClick={() => selectAnswer(option.answer)}
              justifyContent="flex-start"
              textAlign="left"
              data-testid={`onboarding-current-status-${option.answer}`}
            >
              {option.label}
            </OptionRow>
          ))}

          {isOther && (
            <Textarea
              ref={descriptionRef}
              {...DESCRIPTION_FIELD_PROPS}
              value={description}
              onChange={(e) => changeDescription(e.target.value)}
              placeholder="Tell us in your own words"
              data-testid="onboarding-current-status-description"
            />
          )}
        </VStack>
      </VStack>
    </StickyCtaLayout>
  );
}

export default CurrentStatus;
